import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import classes from './RootErrorBoundary.module.css';

export function RootErrorBoundary({ children }: React.PropsWithChildren) {
  return <ErrorBoundary FallbackComponent={RootErrorComponent}>{children}</ErrorBoundary>;
}

// Firefox-specific Error properties
interface FirefoxError extends Error {
  fileName?: string;
  lineNumber?: number;
  columnNumber?: number;
}

/**
 * Extract error details from unknown error type.
 */
function getErrorDetails(error: unknown): {
  name: string;
  message: string;
  stack?: string;
  firefoxError?: FirefoxError;
} {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.toString(),
      stack: error.stack,
      firefoxError: error as FirefoxError,
    };
  }
  return { name: 'Error', message: String(error) };
}

export function RootErrorComponent({ error }: FallbackProps) {
  const details = getErrorDetails(error);
  return (
    <div className={classes.error}>
      <h1>An application error has occurred.</h1>
      <div className={classes.message}>
        <strong>{details.name}:</strong> {details.message}
        {!!details.stack && (
          <>
            {details.firefoxError?.fileName && (
              <div className={classes.location}>
                In <code>{details.firefoxError.fileName}</code> at line {details.firefoxError.lineNumber}:
                {details.firefoxError.columnNumber}
              </div>
            )}
            <pre>{details.stack}</pre>
          </>
        )}
      </div>
    </div>
  );
}
